// ===========================================
// Attendance Trend Chart Component
// ===========================================
// Line chart showing attendance trends over time (weekly aggregation)

import { Card, CardContent, CardHeader, Typography, Box, Divider } from '@mui/material';
import { TrendingUp as TrendingUpIcon } from '@mui/icons-material';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';

import { AttendanceTrendData } from '../../api/reports.api';

// ===========================================
// TYPES
// ===========================================

interface AttendanceTrendChartProps {
  trends: AttendanceTrendData[];
}

// ===========================================
// CUSTOM TOOLTIP
// ===========================================

function CustomTooltip({ active, payload, label }: any) {
  if (!active || !payload || !payload.length) return null;

  return (
    <Card sx={{ p: 1.5, minWidth: 200 }}>
      <Typography variant="body2" fontWeight={600} gutterBottom>
        {label}
      </Typography>
      <Divider sx={{ my: 1 }} />
      {payload.map((entry: any, index: number) => (
        <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
          <Typography variant="caption" sx={{ color: entry.color }}>
            {entry.name}:
          </Typography>
          <Typography variant="caption" fontWeight={600}>
            {typeof entry.value === 'number' ? entry.value.toFixed(1) : entry.value}%
          </Typography>
        </Box>
      ))}
    </Card>
  );
}

// ===========================================
// COMPONENT
// ===========================================

export default function AttendanceTrendChart({ trends }: AttendanceTrendChartProps) {
  // Transform data for chart (convert counts to percentages)
  const chartData = trends.map((trend) => ({
    week: trend.weekLabel,
    'Present %': trend.attendanceRate,
    'Absent %': trend.absentCount > 0
      ? ((trend.absentCount / (trend.presentCount + trend.absentCount + trend.excusedCount)) * 100)
      : 0,
    'Excused %': trend.excusedCount > 0
      ? ((trend.excusedCount / (trend.presentCount + trend.absentCount + trend.excusedCount)) * 100)
      : 0,
  }));

  if (trends.length === 0) {
    return (
      <Card sx={{ mb: 3 }}>
        <CardHeader
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TrendingUpIcon color="primary" />
              <Typography variant="h6">Attendance Trends</Typography>
            </Box>
          }
        />
        <Divider />
        <CardContent>
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">
              No trend data available for the selected period
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ mb: 3 }}>
      <CardHeader
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TrendingUpIcon color="primary" />
            <Typography variant="h6">Attendance Trends</Typography>
          </Box>
        }
        subheader={`Showing ${trends.length} week${trends.length === 1 ? '' : 's'} of data`}
      />
      <Divider />
      <CardContent>
        <ResponsiveContainer width="100%" height={350}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
            <XAxis
              dataKey="week"
              tick={{ fontSize: 12, fill: '#666' }}
              tickLine={false}
            />
            <YAxis
              domain={[0, 100]}
              tick={{ fontSize: 12, fill: '#666' }}
              tickLine={false}
              label={{
                value: 'Percentage',
                angle: -90,
                position: 'insideLeft',
                style: { fontSize: 12, fill: '#666' },
              }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              wrapperStyle={{ fontSize: 12 }}
              iconType="line"
            />
            <Line
              type="monotone"
              dataKey="Present %"
              stroke="#5cb399"
              strokeWidth={2}
              dot={{ fill: '#5cb399', r: 4 }}
              activeDot={{ r: 6 }}
            />
            <Line
              type="monotone"
              dataKey="Absent %"
              stroke="#ff4040"
              strokeWidth={2}
              dot={{ fill: '#ff4040', r: 4 }}
              activeDot={{ r: 6 }}
            />
            <Line
              type="monotone"
              dataKey="Excused %"
              stroke="#e67761"
              strokeWidth={2}
              dot={{ fill: '#e67761', r: 4 }}
              activeDot={{ r: 6 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}
